import React, { useState, useRef, useEffect } from 'react';
import { clientsService } from '../services/api';
import { fmtCNPJ, fmtPhone } from '../utils/formatters';

interface ClientPopoverProps {
  clientId: number | undefined | null;
  children: React.ReactNode;
  /**
   * Ocupa a largura toda em vez de `inline-block`. Numa célula de tabela o
   * inline-block encolhe conforme o conteúdo e o `truncate` do filho deixa de
   * valer, fazendo a razão social invadir a coluna vizinha. Opcional de
   * propósito: mudar o padrão regride Faturas, Recebíveis e Fiscal de uma vez.
   */
  block?: boolean;
}

// Simple in-memory cache to avoid refetching the same client
const clientCache: Record<string | number, any> = {};

export default function ClientPopover({ clientId, children, block }: ClientPopoverProps) {
  const [open, setOpen] = useState(false);
  const [client, setClient] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } // o Esc é do popover: a casca de fora não fecha junto
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!clientId) return;

    if (open) {
      setOpen(false);
      return;
    }

    setOpen(true);

    if (clientCache[clientId]) {
      setClient(clientCache[clientId]);
      return;
    }

    setLoading(true);
    try {
      const allClients = clientCache.__all as any[] | undefined;
      if (allClients) {
        const found = allClients.find((c: any) => c.id === clientId);
        if (found) {
          clientCache[clientId] = found;
          setClient(found);
          setLoading(false);
          return;
        }
      }

      // Fetch all clients once, cache them
      const data = await clientsService.getAll();
      const list = Array.isArray(data) ? data : data.data || [];
      (clientCache as any).__all = list;
      for (const c of list) {
        clientCache[c.id] = c;
      }
      setClient(clientCache[clientId] || null);
    } catch (err) {
      console.error('Erro ao carregar cliente:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!clientId) return <>{children}</>;

  const address = client
    ? [client.street, client.number, client.complement, client.neighborhood, client.city, client.state]
        .filter(Boolean)
        .join(', ')
    : '';

  return (
    <div className={`relative ${block ? 'block min-w-0' : 'inline-block'}`} ref={popoverRef}>
      <button
        type="button"
        onClick={handleClick}
        // `block`: o botão ocupa a célula. Como inline-block ele crescia até o nome inteiro (a largura mínima
        // de um texto `truncate` é o texto todo) e o nome longo vazava da célula em vez de cortar.
        className={`text-left cursor-pointer hover:underline decoration-slate-500 underline-offset-2 ${block ? 'block w-full min-w-0' : ''}`}
      >
        {children}
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-slate-800 border border-slate-600 rounded-xl shadow-2xl p-4 w-[340px] text-sm">
          {loading ? (
            <div className="text-slate-400 text-center py-3">Carregando...</div>
          ) : !client ? (
            <div className="text-slate-400 text-center py-3">Cliente nao encontrado</div>
          ) : (
            <div className="space-y-2.5">
              {/* Header */}
              <div className="pb-2 border-b border-slate-700">
                <div className="text-white font-semibold text-sm">
                  {client.name || client.tradeName || client.companyName}
                </div>
                {client.companyName && client.companyName !== (client.name || client.tradeName) && (
                  <div className="text-slate-400 text-xs mt-0.5">{client.companyName}</div>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    client.status === 'Ativo' ? 'bg-green-500/20 text-green-400' :
                    client.status === 'Inativo' ? 'bg-red-500/20 text-red-400' :
                    'bg-slate-600 text-slate-300'
                  }`}>
                    {client.status || 'N/A'}
                  </span>
                  {client.segment && (
                    <span className="text-slate-500 text-xs">{client.segment}</span>
                  )}
                </div>
              </div>

              {/* Details */}
              <Row label="CNPJ" value={client.cnpj ? fmtCNPJ(client.cnpj) : null} mono />
              <Row label="IE" value={client.inscricaoEstadual} mono />
              <Row label="Telefone" value={client.phone ? fmtPhone(client.phone) : null} />

              {/* Financial emails from client_emails relation */}
              {(() => {
                const financialEmails: string[] = [];
                if (client.emails?.length > 0) {
                  client.emails
                    .filter((e: any) => e.type === 'Financeiro')
                    .forEach((e: any) => financialEmails.push(e.email));
                }
                if (financialEmails.length === 0 && client.emailFinancial) {
                  financialEmails.push(client.emailFinancial);
                }
                if (financialEmails.length === 0) return null;
                return (
                  <div className="flex items-start gap-2">
                    <span className="text-slate-500 text-xs min-w-[110px] shrink-0">Email Financeiro</span>
                    <div className="text-slate-200 text-xs break-all space-y-0.5">
                      {financialEmails.map((em, i) => <div key={i}>{em}</div>)}
                    </div>
                  </div>
                );
              })()}

              <Row label="Email Geral" value={client.emailGeneral} />
              <Row label="Contato Financeiro" value={client.financialContact} />
              <Row label="Contato Tecnico" value={client.technicalContact} />
              {address && <Row label="Endereco" value={address} />}
              {client.zipCode && <Row label="CEP" value={client.zipCode} />}
              <Row label="Vencimento" value={client.dueDay ? `Dia ${client.dueDay}` : null} />
              <Row label="Pagamento" value={client.paymentMethod} />
              <Row label="Envio por Email" value={emailDeliveryLabel(client.emailDeliveryType)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const emailDeliveryLabels: Record<string, string> = {
  nf_fatura: 'NF + Fatura',
  nf_fatura_boleto: 'NF + Fatura + Boleto',
  nf_fatura_relatorio: 'NF + Fatura + Relatorio',
  nf_fatura_boleto_relatorio: 'NF + Fatura + Boleto + Relatorio',
  nenhum: 'Nenhum',
};

function emailDeliveryLabel(type: string | null | undefined): string | null {
  if (!type) return null;
  return emailDeliveryLabels[type] || type;
}

function Row({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <span className="text-slate-500 text-xs min-w-[110px] shrink-0">{label}</span>
      <span className={`text-slate-200 text-xs break-all ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}
